import React, { useState, useEffect, useRef, ReactElement, KeyboardEvent, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { type Dispatch } from 'redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import { actions as projectMgrDashboardActions } from '@Actions/projectMgrDashboardActions';
import { actions as projectActions } from '@Actions/projectActions';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import { actions as filesActions } from '@Actions/filesActions';
import { actions as baselineActions } from '@Actions/baselineActions';
import { DataTable, DataTableColReorderEvent } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import Moment from 'moment';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT, DATETIME_FORMAT, PROJECT_TASKS_LIST_UPDATE_OPTIONS as BulkOptions } from '../../../../constants/constants';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import Modal61 from '@Common/Modal/Modal61';
import { Dropdown } from 'primereact/dropdown';
import { SelectAllCheck, DropdownBaselineTabs, ButtonBaselineTabs, BaselineTaskNameFilter, HeaderButtons, RowGroupHeaderButtons } from './styles';
import CreateTaskForm from '@Components/NewProjectWizard/ContentWidgets/Tasks/CreateTaskForm';
import { Task } from '@Components/NewProjectWizard/Interfaces/Types';
import { FormFilterResponse } from '@Components/NewProjectWizard/Interfaces/PropTypes';
import { Project } from '@Types/common';
import { CreateBaseline, CopyBaselineFromProject, ImportTasksWizzard } from './components/index';
import { daysToDaysWeeks } from '../../../../helpers/dateTime';
import { TruncateText } from '../../../../helpers/strings';
import Icon from '../../../Common/Icon';
import Tag from '../../../Common/Tag/Tag';
import CustomMultiSelect from '../../../Common/PrimeMultiselectBox/CustomMultiSelect';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import BaselineTaskListColumnDrawer from '../../../BaselineTaskList/BaselineTaskListColumnDrawer';
import Routes from '@Constants/routes';
import { getSearchParamSortFields, getDataTableSortFields } from '../../../../helpers/sortHelper';
import BaselinesForm from '../../../Subscription/Admin/BaselinesForm';

const defaultFormData = {
	id: 0,
	name: '',
	description: '',
	contingentId: '',
	phaseId: '',
	departmentId: '',
	communityId: '',
	jobTitleId: '',
	contingentName: '',
	contactName: '',
	contactEmail: '',
	additionalInfo: '',
	taskTagIds: [],
	isActive: true,
	isLocationTaskListActive: true,
	links: [],
	targetDaysOut: '',
	targetDays: '',
	targetWeeks: '',
	targetStaticDate: '',
	startDaysOut: '',
	startDays: '',
	startWeeks: '',
	startStaticDate: '',
	anticipatedEffort: '',
	steps: [],
	prereqTasks: [],
	showStaticDates: false, // only for ui
	createdByName: '', // Only display if Editing
	createdDate: '', // Only display if Editing
	modifiedByName: '', // Only display if Editing
	modifiedDate: '', // Only display if Editing
	isMilestone: false,
};

interface ProjectTasksProps {
	projectData: Project;
	subscriptionId: number;
	userColumns: Array<string>;
	onColumnsChange: (newColumns: Array<string>) => Promise<void>;
}

interface SearchParams {
	projectKey: string;
	baselines: Array<number>;
	baselineTaskName: string;
	isActive: boolean;
	additionalInfo: string;
	departments: Array<number>;
	baselineJobTitles: Array<number>;
	communities: Array<number>;
	phases: Array<number>;
	disciplines: Array<number>;
	tags: Array<number>;
	createdDateStart: Date | null,
	createdDateEnd: Date | null,
	pageSize: number;
	pageNumber: number;
	sortBy: string;
	sortDir: string;
	multiColumnSort: Array<{ [key: string]: string }>;
}

interface BaselineTaskColumn {
	field: string,
	header: string,
	isFeatureEnabled: boolean,
	default: boolean,
	isSortable: boolean,
	body?(rowData: any): React.JSX.Element | React.JSX.Element[] | string | null,
}

interface DatePickerRange {
	rangeName: string;
	start: Date | null;
	end: Date | null;
}

interface ModalRefProps { hide: () => void, show: () => void };

const bulkOptions = [
	{ id: BulkOptions.UpdateAttributes.value, name: BulkOptions.UpdateAttributes.label },
	{ id: BulkOptions.BulkPushdown.value, name: BulkOptions.BulkPushdown.label },
	{ id: BulkOptions.PreviewPushdown.value, name: BulkOptions.PreviewPushdown.label },
	{ id: BulkOptions.OrderTasks.value, name: BulkOptions.OrderTasks.label },
];

const ProjectTasks = ({ projectData, subscriptionId, userColumns, onColumnsChange }: ProjectTasksProps): ReactElement => {
	const [baselines, setBaselines] = useState<Array<any>>([]);
	const [expandedRows, setExpandedRows] = useState(null);
	const [loading, setLoading] = useState<number>(0);
	const [selectedBaselineTasks, setSelectedBaselineTasks] = useState<Array<any>>([]);
	const [baselineId, setBaselineId] = useState<number>(0);
	const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState<boolean>(false);
	const [isBaselineDrawerOpen, setIsBaselineDrawerOpen] = useState<boolean>(false);
	const [formFiltersLoaded, setFormFiltersLoaded] = useState<boolean>(false);
	const [columns, setColumns] = useState<Array<any>>([]);
	const [columnOptions, setColumnOptions] = useState<Array<any>>([]);
	const [datePickerRange, setDatePickerRange] = useState<DatePickerRange>({
		rangeName: '',
		start: null,
		end: null
	});

	const [searchParams, setSearchParams] = useState<SearchParams>({
		projectKey: projectData.projectKey,
		baselines: [],
		baselineTaskName: '',
		isActive: true,
		pageSize: DEFAULT_PAGE_SIZE,
		pageNumber: 1,
		sortBy: '',
		sortDir: '',
		multiColumnSort: [
			{
				sortBy: 'baselineName',
				sortDir: 'asc',
			},
			{
				sortBy: 'baselineTaskName',
				sortDir: 'asc',
			}
		],
		additionalInfo: '',
		departments: [],
		baselineJobTitles: [],
		communities: [],
		phases: [],
		disciplines: [],
		tags: [],
		createdDateStart: null,
		createdDateEnd: null,
	});

	const { sortField, sortOrder } = searchParams.multiColumnSort.length
		? getDataTableSortFields(searchParams.multiColumnSort.find(({ sortBy }) => sortBy === 'baselineName'))
		: getDataTableSortFields(searchParams);

	const [totalCount, setTotalCount] = useState<number>(0);
	const [availableBulkOperations] = useState(bulkOptions);
	const [selectedBulkOperation, setSelectedBulkOperation] = useState(BulkOptions.BulkUpdate.value);

	const activeOptions = [
		{ value: 1, label: 'Import Tasks', icon: 'fa-file-import' },
		{ value: 2, label: 'Add Task', icon: 'fa-share-from-square' },
		{ value: 3, label: 'Copy Tasks from other Project', icon: 'fa-files' },
		{ value: 4, label: 'New Baseline', icon: 'fa-circle-plus' },
	];

	const optionTemplate = (option: any) => {
		const iconName = `fa-regular ${option.icon} me-2`;
		return (
			<div>
				<i className={iconName} /> {option.label}
			</div>
		);
	};

	const [formData, setFormData] = useState({
		id: 0,
		name: '',
		description: '',
		contingentId: '',
		phaseId: '',
		departmentId: '',
		communityId: '',
		jobTitleId: '',
		contingentName: '',
		baselineId: 0,
		contactName: '',
		contactEmail: '',
		additionalInfo: '',
		taskTagIds: [],
		isActive: true,
		isLocationTaskListActive: true,
		links: [],
		targetDaysOut: '',
		targetDays: '',
		targetWeeks: '',
		targetStaticDate: '',
		startDaysOut: '',
		startDays: '',
		startWeeks: '',
		startStaticDate: '',
		anticipatedEffort: '',
		steps: [],
		prereqTasks: [],
		showStaticDates: false, // only for ui
		isMilestone: false,
		baselineIds: [],
		baselineIdsSelected: []
	});

	const [formFilters, setFormFilters] = useState<FormFilterResponse>({
		departments: [],
		baselineJobTitles: [],
		communities: [],
		phases: [],
		disciplines: [],
		certifiedTaskId: 0,
		subscriptionFiles: [],
		taskTags: [],
		isLoaded: false,
		isStartDateActive: false,
		isTaskStepsActive: false,
		isTaskEffortActive: false,
		isTaskFeedbackActive: false,
		isTaskDependenciesActive: false,
		isProjectWidgetActive: false,
		latestBaselineId: 0,
		lowestCommunityId: 0,
		baselineIdsFilter: []
	});

	const [currentFormState, setCurrentFormState] = useState({
		editTask: null as Task | null,
		unchangedTask: null as Task | null,
		newTask: null as Task | null,
		isFormValid: true,
		showDateError: false,
		isLoading: false,
		taskSearchResults: [],
		taskPrereqSearchResults: [],
		unsavedChanges: false,
		saveAndNewRetention: false,
		baselineId: baselineId,
	});

	const dispatch: Dispatch = useDispatch();
	const getFormFilters = async (baselineIdParam: number) => dispatch(newProjectWizardActions.loadFormFilters(subscriptionId, baselineIdParam));
	const saveBaselineTaskNewWizard = async (task: object) => dispatch(newProjectWizardActions.saveBaselineTask(task));
	const searchProjectBaselinesV2 = async (params) => dispatch(projectMgrDashboardActions.searchProjectBaselinesV2(params));
	const unlinkBaseline = async (params) => dispatch(projectMgrDashboardActions.unlinkBaseline(params));
	const listProjectBaselines = async (projectId) => dispatch(projectActions.listProjectBaselines(projectId));
	const downloadFile = async (fileName) => dispatch(filesActions.download(fileName));

	const baselineActionCreators = useMemo(() => bindActionCreators(baselineActions, dispatch), [dispatch]);

	const history = useHistory();

	// Define function to avoid editor issue
	const modalBodyRef = useRef<{ createBaseline: () => Promise<void>, copyBaselines: () => Promise<void> } | null>(null);

	const createTaskModalRef = useRef<ModalRefProps | null>(null);
	const importTasksModalRef = useRef<ModalRefProps | null>(null);
	const copyBaselineModalRef = useRef<ModalRefProps | null>(null);
	const newBaselineModalRef = useRef<ModalRefProps | null>(null);

	const removeBaselineModalRef = useRef<ModalRefProps | null>(null);;

	// This routine is a little oddly named as it originally was used only to fetch form filter data
	// But it also fetches feature toggles used in this page
	const loadFormFilters = async (overrideFiltersLoaded: boolean = false) => {
		if (formFiltersLoaded && !overrideFiltersLoaded)
			return;

		setLoading(prevLoading => prevLoading + 1);

		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		const currentProjectBaselines = await listProjectBaselinesAsync(projectData.id);
		const firstBaselineId = currentProjectBaselines[0]?.id;

		setBaselineId(firstBaselineId);
		updateBaselineId(firstBaselineId);

		try {
			const result = await getFormFilters(firstBaselineId);
			const payload = result.payload;
			if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_SUCCESS) {

				setFormFilters((prevState) => ({
					...prevState,
					...payload,
					baselineIdsFilter: currentProjectBaselines,
				}));

				if (currentProjectBaselines.length === 1) {
					const newBaselineId = firstBaselineId;

					if (baselineId === 0) {
						setBaselineId(newBaselineId);
						updateBaselineId(newBaselineId);
					}

					const updatedState = {
						...currentFormState,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					const updatedFormDataState = {
						...formData,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
						baselineIdsSelected: currentProjectBaselines.map(item => item.id)
					};

					setCurrentFormState(updatedState);
					setFormData(updatedFormDataState);
				} else if (currentProjectBaselines.length > 1 && payload.latestBaselineId > 0) {

					const newBaselineId = baselineId === 0 ? payload.latestBaselineId + 1 : baselineId;

					if (baselineId === 0) {
						setBaselineId(newBaselineId);
						updateBaselineId(newBaselineId);
					}

					const updatedState = {
						...currentFormState,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
					};

					const updatedFormDataState = {
						...formData,
						baselineId: newBaselineId,
						communityId: payload.lowestCommunityId,
						baselineIdsSelected: formData.baselineIdsSelected,
					};

					setCurrentFormState(updatedState);
					setFormData(updatedFormDataState);
				}

				setFormFiltersLoaded(true);
			} else if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setCurrentFormState({
				...currentFormState,
				isLoading: false,
			});
			setLoading(prevLoading => prevLoading - 1);
		}
	};

	const onFormChange = (name, value) => {
		setFormData((prevData) => {
			const updatedFormData = { ...prevData, [name]: value };
			// Update currentFormState directly within this callback to ensure it has the latest formData
			setCurrentFormState((prevState) => ({
				...prevState,
				editTask: updatedFormData,
				unchangedTask: updatedFormData,
				newTask: updatedFormData,
			}));

			return updatedFormData;
		});
	};

	const validateForm = (task) => {
		const { name, jobTitleId, departmentId, communityId } = task;

		// if there are steps, make sure they all have a name
		for (let i = 0; i < task.steps?.length; i++) {
			if (task.steps[i].name == '') {
				return false;
			}
		}

		for (let i = 0; i < task.links?.length; i++) {
			const link = task.links[i];
			if (link.url.trim().length > 0 && link.name.trim().length === 0) {
				// if there are links, make sure they all have abbreviations
				// if there is white space clear it out so element validation is visible
				link.name = '';
				const links = task.links.slice();
				links.splice(i, 1, link);
				//#TODO FIX THIS
				onFormChange('links', links);
				return false;
			}
		}

		if (currentFormState.editTask.showStaticDates && !currentFormState.editTask.targetStaticDate) {
			return false;
		}

		if (
			currentFormState.editTask.showStaticDates === false &&
			(isNaN(currentFormState.editTask.targetWeeks) || currentFormState.editTask.targetWeeks === '')
		) {
			return false;
		}

		if (
			currentFormState.editTask.showStaticDates === false &&
			(isNaN(currentFormState.editTask.targetDays) || currentFormState.editTask.targetDays === '')
		) {
			return false;
		}

		if (name && name.trim().length > 0 && jobTitleId && departmentId && communityId) {
			return true;
		}

		return false;
	};

	const onSave = async (saveDataAndRetention) => {
		if (currentFormState.isLoading) {
			return;
		}

		setCurrentFormState({
			...currentFormState,
			isLoading: true,
		});

		const task = {
			...currentFormState.editTask,
			projectKey: searchParams.projectKey,
		};

		if (saveDataAndRetention) {
			setCurrentFormState({
				...currentFormState,
				saveAndNewRetention: !currentFormState.saveAndNewRetention,
				showDateError: false,
			});
		}

		if (!validateForm(task)) {
			setCurrentFormState((prevState) => ({
				...prevState,
				isFormValid: false,
				isLoading: false,
			}));
			return;
		}

		if (currentFormState.editTask.showStaticDates) {
			// clear out any values in the other section that isn't visible so it does transport to api
			task.targetDaysOut = null;
			task.startDaysOut = null;
			task.targetDays = null;
			task.targetWeeks = null;
			task.startDays = null;
			task.startWeeks = null;
			task.isLocationTaskDateAdjustable = false;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive) {
				if (task.startStaticDate !== undefined || task.targetStaticDate !== undefined) {
					const start = new Date(task.startStaticDate);
					const target = new Date(task.targetStaticDate);

					// NOTE: you can start and finish something the same day
					if (target.getTime() < start.getTime()) {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: true,
						}));
						return;
					} else {
						setCurrentFormState((prevState) => ({
							...prevState,
							showDateError: false,
						}));
					}
				}
			}
		} else {
			// days and weeks
			const targetWeeks = task.targetWeeks === null ? 0 : parseInt(task.targetWeeks);
			const targetDays = task.targetDays === null ? 0 : parseInt(task.targetDays);
			const startWeeks = task.startWeeks === null ? 0 : parseInt(task.startWeeks || 0);
			const startDays = task.startDays === null ? 0 : parseInt(task.startDays || 0);

			// convert target/start dates to total days (values can be negative!)
			task.targetDaysOut = targetWeeks * 7 + targetDays;
			task.startDaysOut = startWeeks * 7 + startDays;

			task.startStaticDate = null;
			task.targetStaticDate = null;

			// validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
			if (formFilters.isStartDateActive && task.startDaysOut > 0) {
				if (task.targetDaysOut > task.startDaysOut) {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: true,
					}));
					return;
				} else {
					setCurrentFormState((prevState) => ({
						...prevState,
						showDateError: false,
					}));
				}
			} else {
				task.startDaysOut = null;
			}
		}

		// if IsActive make sure isLocationTaskListActive is true
		if (task.isActive) {
			task.isLocationTaskListActive = true;
		}

		// Remove any blank links
		if (task.links && task.links.length) {
			task.links = task.links.filter((x) => x.name && x.name.trim().length && x.url && x.url.trim().length);
		}

		//transforms taskTagIds from string to array of strings
		if (task.taskTagIds && task.taskTagIds.length > 0) {
			task.taskTagIds = !Array.isArray(task.taskTagIds) ? task.taskTagIds.split(',') : task.taskTagIds.join().split(',');
		}

		setCurrentFormState((prevState) => ({
			...prevState,
			isFormValid: true,
			isLoading: true,
		}));

		try {
			task.baselineIdsSelected = task.baselineIdsSelected && task.baselineIdsSelected.length > 0 ? task.baselineIdsSelected.split(',').map(Number) : null;

			const response = await saveBaselineTaskNewWizard(task);
			if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_SUCCESS) {
				Notification('success', 'Success', 'Baseline Task Created');
				const currentBaselineId = currentFormState.editTask?.baselineId;
				if (saveDataAndRetention) {
					// retain these properties and keep the drawer open
					const retainProps = {
						isMilestone: currentFormState.editTask?.isMilestone,
						jobTitleId: currentFormState.editTask?.jobTitleId,
						departmentId: currentFormState.editTask?.departmentId,
						communityId: currentFormState.editTask?.communityId,
						phaseId: currentFormState.editTask?.phaseId,
						baselineId: currentBaselineId,
						isActive: currentFormState.editTask?.isActive,
						baselineIdsSelected: currentFormState.editTask?.baselineIdsSelected,
					};

					const newTask = {
						...defaultFormData,
						...retainProps,
					};

					if (response.payload.baselineId && response.payload.baselineId !== 0) updateBaselineId(response.payload.baselineId);

					onDrawerComplete(currentBaselineId);

					setFormData(newTask);

					setCurrentFormState((prevState) => ({
						...prevState,
						editTask: newTask,
						isLoading: false,
						saveAndNewRetention: false,
						baselineId: baselineId,
						showDateError: false,
					}));
				} else {
					//We assign this to a new baseline. wILL BE LOADED IN FORM FILTERS
					onDrawerComplete();
					setCurrentFormState({
						...currentFormState,
						editTask: formData,
						unchangedTask: formData,
						newTask: formData,
						currentTask: formData,
					});

					createTaskModalRef.current?.hide();
				}
			} else if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
				setCurrentFormState((prevState) => ({
					...prevState,
					isLoading: false,
				}));
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));
		} finally {
			setCurrentFormState((prevState) => ({
				...prevState,
				isLoading: false,
			}));

			search(searchParams);
		}
	};

	const resetFormData = (baselineId) => {
		setFormData({
			id: 0,
			name: '',
			description: '',
			contingentId: '',
			phaseId: '',
			departmentId: '',
			communityId: '',
			jobTitleId: '',
			contingentName: '',
			baselineId: baselineId,
			contactName: '',
			contactEmail: '',
			additionalInfo: '',
			taskTagIds: [],
			isActive: true,
			isLocationTaskListActive: true,
			links: [],
			targetDaysOut: '',
			targetDays: '',
			targetWeeks: '',
			targetStaticDate: '',
			startDaysOut: '',
			startDays: '',
			startWeeks: '',
			startStaticDate: '',
			anticipatedEffort: '',
			steps: [],
			prereqTasks: [],
			showStaticDates: false,
			//createdByName: '',
			//createdDate: '',
			//modifiedByName: '',
			//modifiedDate: '',
			isMilestone: false,
			baselineIds: [],
			baselineIdsSelected: []
		});
	};

	const onDrawerComplete = (baselineId = 0) => {
		if (baselineId === 0) {
			createTaskModalRef.current?.hide();
		}
		resetFormData(0);

		setCurrentFormState({
			...currentFormState,
			editTask: formData,
			unchangedTask: formData,
			newTask: formData,
			currentTask: formData,
		});
	};

	const checkForChanges = () => {
		// compare unChangedTask to editTask if editing a task
		if (currentFormState.editTask.id != 0) {
			const haveTheSameValues = _.isEqual(currentFormState.unchangedTask, currentFormState.editTask);
			setCurrentFormState((prevState) => ({
				...prevState,
				unsavedChanges: !haveTheSameValues,
			}));
		}
	};

	const updateBaselineId = (newBaselineId: number) => {
		setBaselineId(newBaselineId);
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setCurrentFormState((prevState) => ({
			...prevState,
			baselineId: newBaselineId,
		}));
		setFormFilters((prevState) => ({
			...prevState,
			latestBaselineId: newBaselineId,
		}));
	};

	const updateSearchParams = (name: string, value: any): boolean => {
		if (searchParams[name] != value) {
			setSearchParams({
				...searchParams,
				[name]: value
			});

			return true;	// search params were changed
		}

		return false;		// no change to search params
	};

	const search = async (params: SearchParams) => {
		setLoading(prevLoading => prevLoading + 1);
		try {
			const result = await searchProjectBaselinesV2(params);
			const payload = result.payload;
			if (result.type === types.LOAD_PROJECT_BASELINES_SUCCESS_V2) {
				setSelectedBaselineTasks([]);
				setBaselines(payload.results);
				setTotalCount(payload.totalCount);
			} else if (result.type === types.LOAD_PROJECT_BASELINES_FAILURE_V2) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setLoading(prevLoading => prevLoading - 1);
		}
	};

	const onColumnsClick = () => {
		setIsColumnDrawerOpen(true);
	};

	const onDrawerClose = () => {
		setIsColumnDrawerOpen(false);
		setIsBaselineDrawerOpen(false);
	};

	const onColumnToggle = async (name, value) => {
		const cols = columns.slice();
		const modifiedColumn = columnOptions.find((m) => m.value.field === name);

		if (value) {
			cols.push(modifiedColumn.value);
		} else {
			cols.splice(columns.indexOf(modifiedColumn.value), 1);
		}

		setColumns(cols)

		// Save columns personalization
		await onColumnsChange(cols.map(c => c.field));
	};

	const onColumnReorder = async (e: DataTableColReorderEvent) => {
		// Save columns personalization
		const newColumns: Array<string | undefined> = e.columns.map(c => c.props.field);
		await onColumnsChange(newColumns);
	};

	const selectBulkOperation = async () => {
		switch (selectedBulkOperation) {
			case BulkOptions.UpdateAttributes.value:
				break;
			case BulkOptions.BulkPushdown.value:
				break;
			case BulkOptions.PreviewPushdown.value:
				break;
			case BulkOptions.OrderTasks.value:
				break;
			default:
				break;
		}
	};

	// Function to handle "Select All" checkbox click
	const toggleSelectAll = () => {
		if (selectedBaselineTasks.length === baselines.length) {
			// All rows are selected, so clear selection
			setSelectedBaselineTasks([]);
		} else {
			// Not all rows are selected, so select all
			setSelectedBaselineTasks([...baselines]);
		}
	};

	const headerComponent =
		<div className='row'>
			<div className='col-md-6'>
				<HeaderButtons>
					<SelectAllCheck>
						<Checkbox
							onChange={toggleSelectAll} // Handle the "Select All" checkbox change
							checked={selectedBaselineTasks.length === baselines.length}
						/>
					</SelectAllCheck>
					<MultiSelectBox
						name='views'
						idField='id'
						valueField='name'
						placeholder='Select Bulk Action'
						className={'multi-select-bulk'}
						options={availableBulkOperations}
						onChange={(e: string) => setSelectedBulkOperation(e)}
						selectedValues={selectedBulkOperation}
						isMulti={false}
						isSearchable={false}
					/>
					<Button label='Apply' onClick={selectBulkOperation} disabled={selectedBaselineTasks.length === 0} className='pacer-table-button' />
				</HeaderButtons>
			</div>
			<div className='col-md-6'>
				<div className='d-flex justify-content-end'>
					<Button icon="fa-regular fa-columns-3 fa-xl" title='Choose Columns' onClick={onColumnsClick} className='pacer-table-button' />
				</div>
			</div>
		</div >

	const checkboxTemplate = (rowData) => {
		return (
			<Checkbox
				name='checkbox'
				checked={selectedBaselineTasks.some((b) => b.baselineTaskId === rowData.baselineTaskId)}
				onChange={() => toggleRowSelection(rowData)}
			/>
		);
	};

	const onPageChange = async (e) => {
		const updatedParams = {
			...searchParams,
			pageNumber: e.page + 1,
			pageSize: e.rows,
		};

		setSearchParams(updatedParams);
	};

	// Function to handle individual row checkbox click
	const toggleRowSelection = (row) => {
		if (selectedBaselineTasks.some((b) => b.baselineTaskId === row.baselineTaskId)) {
			// Remove the row based on its id
			setSelectedBaselineTasks(selectedBaselineTasks.filter((b) => b.baselineTaskId !== row.baselineTaskId));
		} else {
			// Add the row to selected locations
			setSelectedBaselineTasks([...selectedBaselineTasks, row]);
		}
	};

	const keyUpFilter = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			const target = event.target as HTMLInputElement;
			updateSearchParams(target.name, target.value);
		}
	};

	const listProjectBaselinesAsync = async (projectId: number) => {
		setLoading(prevLoading => prevLoading + 1);
		try {
			const result = await listProjectBaselines(projectId);

			if (result.type === types.LIST_PROJECT_BASELINES_SUCCESS) {
				const currentBaselineIds = result.payload.baselines.map(item => ({
					id: item.id,
					name: item.name
				}));

				if (currentBaselineIds.length === 1) {
					setFormData({ ...formData, baselineIdsSelected: currentBaselineIds.map(item => item.id) });
				}

				// Return the baselines at the end of the success block
				return currentBaselineIds;
			} else if (result.type === types.LIST_PROJECT_BASELINES_FAILURE) {
				Notification('error', 'An Error Occurred', result.payload.message);
				return [];
			}
		} catch (error) {
			Notification('error', 'Error', error);
			return [];
		} finally {
			setLoading(prevLoading => prevLoading - 1);
		}
	};

	const onExpandTasks = (baselineId: number) => {
		history.push(Routes.BaselineTaskList + "/?bid=" + baselineId);
	};

	const onEditBaseline = (baselineId: number) => {
		setBaselineId(baselineId);
		setIsBaselineDrawerOpen(true);
	};

	const onBaselineDrawerSave = () => {
		onDrawerClose();

		if (!updateSearchParams('pageNumber', 1))
			search(searchParams);
	}

	useEffect(() => {
		// Update currentFormState whenever baselineId changes
		setCurrentFormState({ ...currentFormState, baselineId: baselineId });
		setFormData({ ...formData, baselineId: baselineId });
	}, [baselineId]);

	useEffect(() => {
		search(searchParams);
	}, [searchParams]);

	useEffect(() => {
		// load feature toggles & build columns on page load
		async function pageLoadFetch() {
			await loadFormFilters();
			//buildColumnOptions();
		}

		pageLoadFetch()
	}, []);

	useEffect(() => {
		buildColumnOptions();
	}, [formFilters]);

	const getStaticColumns = (): Array<BaselineTaskColumn> => {
		return [
			{
				field: 'baselineName',
				header: 'Task Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
				body: taskNameTemplate,
			},
			{
				field: 'startDaysOut',
				header: 'Relative Start',
				isFeatureEnabled: formFilters.isStartDateActive,
				body: startDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetDaysOut',
				header: 'Relative Target',
				isFeatureEnabled: true,
				body: targetDaysOutTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'startStaticDate',
				header: 'Static Start',
				isFeatureEnabled: formFilters.isStartDateActive,
				body: startStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'targetStaticDate',
				header: 'Static Target',
				isFeatureEnabled: true,
				body: targetStaticDateTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'baselineJobTitle',
				header: 'Job Title',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'department',
				header: 'Department Name',
				isFeatureEnabled: true,
				default: true,
				isSortable: true,
			},
			{
				field: 'additionalInfo',
				header: 'Additional Information',
				isFeatureEnabled: true,
				default: true,
				body: additionalInfoTemplate,
				isSortable: true,
			},
			{
				field: 'anticipatedEffort',
				header: 'Anticipated Effort',
				isFeatureEnabled: formFilters.isTaskEffortActive,
				default: false,
				isSortable: true,
			},
			{
				field: 'ordinal',
				header: 'Task Order',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
			},
			{
				field: 'discipline',
				header: 'Discipline',
				isFeatureEnabled: true,
				isSortable: true,
				default: false,
			},
			{
				field: 'community',
				header: 'Community',
				isFeatureEnabled: true,
				isSortable: true,
				default: false,
			},
			{
				field: 'phase',
				header: 'Phase',
				isFeatureEnabled: true,
				isSortable: true,
				default: false,
			},
			{
				field: 'totalDependencies',
				header: '# of Dependencies',
				isFeatureEnabled: formFilters.isTaskDependenciesActive,
				isSortable: true,
				default: false,
			},
			{
				field: 'totalSubtasks',
				header: '# of Steps',
				isFeatureEnabled: formFilters.isTaskStepsActive,
				default: true,
				isSortable: true,
			},
			{
				field: 'createdDate',
				header: 'Created Date',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: createdDateTemplate,
			},
			{
				field: 'modifiedDate',
				header: 'Last Modified',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: modifiedDateTemplate,
			},
			{
				field: 'isActive',
				header: 'Active',
				isFeatureEnabled: true,
				body: activeTemplate,
				default: true,
				isSortable: true,
			},
			{
				field: 'links',
				header: 'Links',
				isFeatureEnabled: true,
				body: linksTemplate,
				default: false,
				isSortable: false,
			},
			{
				field: 'tags',
				header: 'Tags',
				isFeatureEnabled: true,
				default: false,
				isSortable: true,
				body: tagsTemplate,
			},
			{
				field: 'IsMilestone',
				header: 'Milestone',
				isFeatureEnabled: true,
				isSortable: true,
				body: milestoneTemplate
			}
		];
	};

	const buildColumnOptions = () => {

		const columns = getStaticColumns().slice();
		const selectedColumns = [];

		// Look for columns personalization, else use the default
		if (userColumns && userColumns.length) {
			for (const uc of userColumns) {
				const col = columns.find(c => c.field === uc);
				if (col) {
					selectedColumns.push(col);
				}
			}
		} else {
			for (const col of columns) {
				if (col.default === true) {
					selectedColumns.push(col);
				}
			}
		}

		const columnOptions = [];
		for (const col of columns) {
			if (col.isFeatureEnabled) {
				columnOptions.push({ label: col.header, value: col });
			}
		}

		setColumns(selectedColumns);
		setColumnOptions(columnOptions);
	};

	const rowGroupHeaderTemplate = (data) => {
		return (
			<React.Fragment>
				<div className="d-inline-flex justify-content-between fw-bold" style={{ 'width': '96%' }}>
					<div className="p-2">{data.baselineName}</div>
					<RowGroupHeaderButtons>
						<Button
							icon='far fa-pen-to-square'
							onClick={() => onEditBaseline(data.baselineId)}
							title='Edit Baseline'
							className='fa-lg'
						/>
						<Button
							icon='far fa-arrow-up-right-from-square'
							onClick={() => onExpandTasks(data.baselineId)}
							title='Expand Tasks'
							className='fa-lg'
						/>
						<Button
							icon='far fa-circle-plus'
							onClick={async () => {
								try {
									setFormData({ ...formData, baselineIdsSelected: `${data.baselineId}` });
									createTaskModalRef.current?.show();
								} catch (error) {
									console.error("Error while adding baseline task:", error);
								}
							}}
							title='Add Baseline Task'
							className='fa-lg'
						/>
						<Button
							icon='far fa-trash-can'
							onClick={() => showUnlinkModal(data.baselineId)}
							title='"Remove Baseline'
							className='fa-lg trash-can'
						/>
					</RowGroupHeaderButtons>
				</div>
			</React.Fragment>
		);
	};

	const taskNameTemplate = (rowData) => {
		return <div className='show-newlines'>{rowData.baselineTaskName}</div>;
	};

	const startDaysOutTemplate = (rowData) => {
		return daysToDaysWeeks(rowData.startDaysOut);
	};

	const targetDaysOutTemplate = (rowData) => {
		return daysToDaysWeeks(rowData.targetDaysOut);
	};

	const startStaticDateTemplate = (rowData) => {
		if (rowData.startStaticDate) {
			return Moment.utc(rowData.startStaticDate).format(DATE_FORMAT);
		}

		return '';
	};

	const targetStaticDateTemplate = (rowData) => {
		if (rowData.targetStaticDate) {
			return Moment.utc(rowData.targetStaticDate).format(DATE_FORMAT);
		}

		return null;
	};

	const additionalInfoTemplate = (rowData) => {
		return <div className='show-newlines'>{TruncateText(rowData.additionalInfo, 200)}</div>;
	};

	const activeTemplate = (rowData) => {
		if (rowData.isActive) {
			return (
				<div className='text-success text-center'>
					<Icon name='check-circle' />
				</div>
			);
		} else {
			return (
				<div className='text-danger text-center'>
					<Icon name='times-circle' />
				</div>
			);
		}
	};

	const createdDateTemplate = (rowData) => {
		return Moment.utc(rowData.createdDate).format(DATETIME_FORMAT);
	};

	const modifiedDateTemplate = (rowData) => {
		return Moment.utc(rowData.modifiedDate).format(DATETIME_FORMAT);
	};

	const linksTemplate = (rowData) => {
		const retval: React.JSX.Element[] = [];

		if (rowData.links && rowData.links.length > 0) {
			for (let i = 0; i < rowData.links.length; i++) {
				const link = rowData.links[i];

				if (link.customerFileId) {
					retval.push(
						<button key={link.ordinal} type='button' className='btn btn-link' onClick={(e) => onDownloadLink(link, e)}>
							{link.name}
						</button>
					);
				} else {
					let protocol = '';
					const linkUrl = link.url.toLowerCase();
					if (!linkUrl.startsWith('http')) protocol = 'http://';

					retval.push(
						<div key={link.ordinal}>
							<a href={protocol + link.url} rel='noopener noreferrer' target='_blank'>
								{link.name}
							</a>
						</div>
					);
				}
			}
		}

		return retval;
	};

	const tagsTemplate = (rowData) => {
		const retval: React.JSX.Element[] = [];

		if (rowData.tags && rowData.tags.length > 0) {
			for (let i = 0; i < rowData.tags.length; i++) {
				const tag = rowData.tags[i];

				retval.push(
					<div className='mb-1' key={tag.name}>
						<Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
					</div>
				);
			}
		}
		return retval;
	};

	const milestoneTemplate = (rowData) => {
		if (rowData.isMilestone) {
			return (
				<div className='text-warning milestone' title='Milestone Task'>
					&#x25C6;
				</div>
			);
		}
	};

	const renderColumns = () => {
		const activeOptions = [
			{ label: 'Yes', value: true },
			{ label: 'No', value: false },
			{ label: 'All', value: null },
		];

		const filters = {
			baselineName: (
				<BaselineTaskNameFilter>
					<CustomMultiSelect
						className='ui-column-filter'
						optionLabel='name'
						dataKey='id'
						placeholder='Baseline'
						value={searchParams.baselines}
						options={formFilters.baselineIdsFilter}
						onChange={(e) => onCustomMultiSelectChange('baselines', e)}
						onButtonClick={() => { /*Do nothing*/ }}
					/>
					<input
						type='text'
						name='baselineTaskName'
						className='p-inputtext'
						size={10}
						onBlur={onSearchBlur}
						onKeyUp={keyUpFilter}
						placeholder='Task Name'
					/>
				</BaselineTaskNameFilter>
			),
			additionalInfo: (
				<input
					type='text'
					name='additionalInfo'
					className='p-inputtext'
					size={10}
					onBlur={onSearchBlur}
					onKeyUp={keyUpFilter}
					placeholder='Additional Info'
				/>
			),
			department: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					dataKey='id'
					value={searchParams.departments}
					options={formFilters.departments}
					onChange={(e) => onCustomMultiSelectChange('departments', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
			baselineJobTitle: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					dataKey='id'
					value={searchParams.baselineJobTitles}
					options={formFilters.baselineJobTitles}
					onChange={(e) => onCustomMultiSelectChange('baselineJobTitles', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
			community: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					dataKey='id'
					value={searchParams.communities}
					options={formFilters.communities}
					onChange={(e) => onCustomMultiSelectChange('communities', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
			phase: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					dataKey='id'
					value={searchParams.phases}
					options={formFilters.phases}
					onChange={(e) => onCustomMultiSelectChange('phases', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
			discipline: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					dataKey='id'
					value={searchParams.disciplines}
					options={formFilters.disciplines}
					onChange={(e) => onCustomMultiSelectChange('disciplines', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
			isActive: (
				<Dropdown
					className='ui-column-filter'
					value={searchParams.isActive}
					options={activeOptions}
					onChange={onActiveChange}
				/>
			),
			createdDate: (
				<Button
					type='button'
					className='light ui-column-filter'
					name='createdDateRangeButton'
					label={searchParams.createdDateStart || searchParams.createdDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => showDateRangePicker('createdDate')}
				/>
			),
			tags: (
				<CustomMultiSelect
					className='ui-column-filter'
					optionLabel='name'
					placeholder='Filter'
					dataKey='id'
					value={searchParams.tags}
					options={formFilters.taskTags}
					onChange={(e) => onCustomMultiSelectChange('tags', e)}
					onButtonClick={() => { /*Do nothing*/ }}
				/>
			),
		};

		const cols = columns.map((col) => {
			const filterElement = filters[col.field];
			// HACK: (jwv) PrimeReact's MultiSelect is throwing an error any time we deselect a column from the column selector
			// when it's internal object comparison code tries to set the _$visible property on a style object defined in our
			// columns objects.  IsActive was the only column with style object, and it was setting the width to 80px, so this
			// hack is in place to explicitly set that on the column here, instead of definining the style object as a member
			// of the column definition.
			if (col.field === 'isActive') {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
						style={{ width: '80px' }}
					/>
				);
			} else if (filterElement && col.isFeatureEnabled) {
				return (
					<Column
						key={col.field}
						sortable={col.isSortable}
						field={col.field}
						filter={true}
						filterElement={filterElement}
						header={col.header}
						body={col.body}
					/>
				);
			} else if (col.isFeatureEnabled) {
				return <Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} />;
			} else {
				return null;
			}
		});

		return cols;
	};

	const onDownloadLink = async (link, e) => {
		e.preventDefault();
		try {
			// extract the file name from the link.url
			const start = link.url.indexOf('=') + 1;
			const fileName = link.url.substr(start, link.url.length - start);

			await downloadFile(fileName);
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const onSearchBlur = (e) => {
		updateSearchParams(e.target.name, e.target.value);
	};

	const onActiveChange = (e) => {
		updateSearchParams('isActive', e.value);
	};

	const onCustomMultiSelectChange = (name: string, e: any) => {
		updateSearchParams(name, (e || []).map(item => item.id));
	};

	const showDateRangePicker = (rangeName) => {
		const start = searchParams.createdDateStart ? searchParams.createdDateStart : null;
		const end = searchParams.createdDateEnd ? searchParams.createdDateEnd : null;

		const newDateRange = {
			...datePickerRange,
			name: rangeName
		};

		if (typeof end === 'string') {
			newDateRange.end = Moment.utc(Date.parse(end));
		}
		if (typeof start === 'string') {
			newDateRange.start = Moment.utc(Date.parse(start));
		}

		setDatePickerRange(newDateRange);
		// setShowingDateRangePicker(true);
	};

	const onOptionsChange = (selectedOption: { value: number }) => {
		switch (selectedOption.value) {
			case 1:
				importTasksModalRef.current?.show();
				break;
			case 2:
				createTaskModalRef.current?.show();
				break;
			case 3:
				copyBaselineModalRef.current?.show();
				break;
			case 4:
			default:
				newBaselineModalRef.current?.show();
				break;
		}
	};

	const onSort = ({ sortField, sortOrder }) => {

		// Step 1: Group data by baselineName
		const groupedData = baselines.reduce((acc, item) => {
			if (!acc[item.baselineName]) {
				acc[item.baselineName] = [];
			}
			acc[item.baselineName].push(item);
			return acc;
		}, {});

		// Step 2: Sort items inside each group
		Object.keys(groupedData).forEach((group) => {
			groupedData[group].sort((a, b) => {
				const valueA = a[sortField] ?? "";
				const valueB = b[sortField] ?? "";

				if (typeof valueA === "string" && typeof valueB === "string") {
					return valueA.localeCompare(valueB) * sortOrder;
				} else {
					return (valueA - valueB) * sortOrder;
				}
			});
		});

		// Step 3: Reconstruct sorted array while maintaining group order
		const sortedData = Object.entries(groupedData).flatMap(([_, group]) => group);

		// Step 4: Update DataTable state
		setBaselines(sortedData);
	};

	const showUnlinkModal = async (baselineId: number) => {
		setBaselineId(baselineId);
		removeBaselineModalRef.current?.show();
	};

	const onRemoveBaseline = async (baselineId: number) => {

		removeBaselineModalRef.current?.hide();

		// De-link the baseline from the project
		setLoading(prevLoading => prevLoading + 1);
		try {
			const request = { baselineId, projectId: projectData.id };
			const result = await unlinkBaseline(request);

			if (result.type === types.UNLINK_BASELINE_SUCCESS) {
				// Refresh the list
				if (!updateSearchParams('pageNumber', 1))
					await search(searchParams);
			} else if (result.type === types.UNLINK_BASELINE_FAILURE) {
				Notification('error', 'An Error Occurred', result.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setLoading(prevLoading => prevLoading - 1);
		}
	};

	const onModalOperationCompletes = async (modalRef: React.MutableRefObject<ModalRefProps | null>) => {
		modalRef.current?.hide();

		await Promise.all([loadFormFilters(true), search(searchParams)]);
	};

	return (
		<div className='publish-content'>
			<div className='d-flex justify-content-end'>
				<ButtonBaselineTabs>
					<button
						type='button'
						className='pacer61-primary-purple-btn my-3 rounded-left'
						onClick={async () => {
							try {
								createTaskModalRef.current?.show();
							} catch (error) {
								console.error("Error while adding baseline task:", error);
							}
						}}>
						<i className='fa-regular fa-share-from-square'></i>
						<span className='mx-2'>Add</span>
					</button>
				</ButtonBaselineTabs>
				<DropdownBaselineTabs>
					<Dropdown name="isActive"
						className='dropdown-baseline-tabs'
						style={{ textAlign: 'left', width: '35px', backgroundColor: '#6860e4', color: 'white', fontFamily: 'Greycliff', fontWeight: '400', fontSize: '12px' }}
						options={activeOptions}
						itemTemplate={optionTemplate}
						onChange={onOptionsChange} />
				</DropdownBaselineTabs>
			</div>

			<DataTable
				value={baselines}
				rowGroupMode="subheader"
				groupRowsBy="baselineName"
				sortMode="single"
				sortField={sortField}
				sortOrder={sortOrder}
				expandableRowGroups
				expandedRows={expandedRows}
				onRowToggle={(e) => setExpandedRows(e.data)}
				rowGroupHeaderTemplate={rowGroupHeaderTemplate}
				header={headerComponent}
				className='p-datatable-sm p-datatable-striped'
				loading={loading !== 0}
				filterDisplay="row"
				reorderableColumns
				onSort={onSort}
				onColReorder={(e) => onColumnReorder(e)}
			>
				<Column
					field='IsChecked'
					body={checkboxTemplate}
					style={{ textAlign: 'center', width: '40px' }}
					reorderable={false}
				/>
				{renderColumns()}
			</DataTable>

			<Paginator
				leftContent={<span>Rows per page:</span>}
				rows={searchParams.pageSize}
				totalRecords={totalCount}
				first={(searchParams.pageNumber - 1) * searchParams.pageSize}
				rowsPerPageOptions={[20, 50, 100]}
				onPageChange={onPageChange}
				template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
				currentPageReportTemplate='{first}-{last} of {totalRecords}'
			/>

			{isColumnDrawerOpen && (
				<BaselineTaskListColumnDrawer
					onComplete={onDrawerClose}
					onColumnToggle={onColumnToggle}
					columnOptions={columnOptions}
					columns={columns}
				/>
			)}

			{isBaselineDrawerOpen && (
				<BaselinesForm
					cancelClick={onDrawerClose}
					saveClick={onBaselineDrawerSave}
					baselineActions={baselineActionCreators}
					baselineId={baselineId}
					subscriptionId={subscriptionId.toString()}
				/>
			)}

			<Modal61
				ref={createTaskModalRef}
				title='Create New Baseline Task'
				body={
					<CreateTaskForm
						currentTask={formData}
						isFormValid={currentFormState.isFormValid}
						onFormChange={onFormChange}
						checkForChanges={checkForChanges}
						loadFormFilters={loadFormFilters}
						formFilters={formFilters}
						baselineId={baselineId}
						showDateError={currentFormState.showDateError}
						isFromProject={true}
					/>
				}
				primaryButtonText='Save Task and Create New'
				secondaryButtonText='Save Task and Close'
				onPrimaryClick={() => onSave(true)}
				onSecondaryClick={() => onSave(false)}
				onCloseClick={() => createTaskModalRef.current?.hide()}
				isLoading={false} />

			<Modal61
				ref={importTasksModalRef}
				title='Import Tasks'
				body={<ImportTasksWizzard 
					projectData={projectData}
					onModalOperationCompletes={() => { onModalOperationCompletes(importTasksModalRef) }}
					formFilters={formFilters} />}
				customFooter={<div>
					<button
						type='button'
						className={'btn btn-primary'}
						data-dismiss='modal'
						onClick={() => importTasksModalRef.current?.hide()}>
						Cancel
					</button>
				</div>}
				onPrimaryClick={undefined}
				onSecondaryClick={undefined}
				onCloseClick={() => importTasksModalRef.current?.hide()}
				primaryButtonText={''}
				isLoading={false} />

			<Modal61
				ref={copyBaselineModalRef}
				title='Copy Baselines'
				body={<CopyBaselineFromProject
					ref={modalBodyRef}
					subscriptionId={subscriptionId}
					projectData={projectData}
					onCopyBaselineComplete={() => { onModalOperationCompletes(copyBaselineModalRef) }} />}
				primaryButtonText='Copy Baselines'
				secondaryButtonText='Cancel'
				onPrimaryClick={() => {
					if (modalBodyRef.current) {
						modalBodyRef.current.copyBaselines();
					}
				}}
				onSecondaryClick={() => copyBaselineModalRef.current?.hide()}
				onCloseClick={() => copyBaselineModalRef.current?.hide()}
				isLoading={false} />

			<Modal61
				ref={newBaselineModalRef}
				title='New Baseline'
				body={<CreateBaseline
					ref={modalBodyRef}
					subscriptionId={subscriptionId}
					projectKey={projectData.projectKey}
					onBaselineCreationComplete={() => { onModalOperationCompletes(newBaselineModalRef) }} />}
				primaryButtonText='Save Baseline'
				secondaryButtonText='Cancel'
				onPrimaryClick={() => {
					if (modalBodyRef.current) {
						modalBodyRef.current.createBaseline();
					}
				}}
				onSecondaryClick={() => newBaselineModalRef.current?.hide()}
				onCloseClick={() => newBaselineModalRef.current?.hide()}
				isLoading={false} />

			<Modal61
				ref={removeBaselineModalRef}
				title='Are you sure?'
				body={
					<p>
						Do you want to remove this baseline and tasks from the project? These tasks will not be included when you publish this project in the future. If these tasks were published to location or teams already, those tasks will not be affected.
					</p>
				}
				primaryButtonText='Yes'
				onPrimaryClick={() => onRemoveBaseline(baselineId)}
				showSecondaryButton={true}
				onSecondaryClick={undefined}
				onCloseClick={undefined}
				isLoading={false}
				isCentered={false}
			/>
		</div>
	);
};

export default ProjectTasks;
